import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useEventLogging } from '@innovamat/event-logging';
import type { State } from '@innovamat/glimmer-components';
import {
  Button,
  InnerHtml,
  Loader,
  Popover,
  STATES,
  Typography,
} from '@innovamat/glimmer-components';
import type { IconType } from '@innovamat/glimmer-icons';
import { downloadBase64PdfFile } from '@innovamat/radiance-utils';

import { isAvailableForDownload } from '../../../../../../modules/report/report';
import type { Test, TestType } from '../../../../../../modules/test/test';
import { formatDate } from '../../../../../../utils/date';
import { useClassroomTestRoundReport } from '../../../../hooks/use-classroom-test-round-report';
import { useClassroomTestRoundReportLanguages } from '../../../../hooks/use-classroom-test-round-report-languages';

const LoadingPopoverItem = styled(Popover.ContentItem)`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DisabledWrapper = styled.div`
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
`;

const AvailabilityText = styled(Typography.Body2)`
  color: ${({ theme }) =>
    theme.tokens.color.alias.cm.text['text-subtle'].value};
`;

type Props = {
  testRoundId: string;
  testRoundName: string;
  report: Test['report'];
  testType?: TestType;
  testId: string;
  classroomId: string;
};

const DownloadTestReportCell = ({
  testRoundId,
  testRoundName,
  report,
  testType,
  testId,
  classroomId,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const { setEventData } = useEventLogging();

  const [language, setLanguage] = useState<string>('');
  
  const isFakeData = testRoundId.includes('fake-data');
  
  const { data: testReportLanguages, isFetching: isFetchingLanguages, refetch: fetchReportLanguages } = useClassroomTestRoundReportLanguages({
    testRoundId,
  });

  const { data: reportFile, isFetching: isFetchingReport, isSuccess, isError, refetch: fetchReport } = useClassroomTestRoundReport({
    language,
    testRoundId,
  });

  const handleReportLanguageItemClick = async (
    languageSelected: string
  ): Promise<void> => {
    if (isFakeData) {
      try {
        // Solo carga el archivo cuando se selecciona el idioma
        const module = await import(
          `./fake-reports-data/${testType}-fake-report-${languageSelected}.ts`
        );
        const pdfBase64 = module.default; // o module.nombreExportado
        downloadReport(pdfBase64);
        setLanguage('');
      } catch (error) {
        console.error(
          `Error al cargar el informe para ${languageSelected}:`,
          error
        );
        setLanguage('');
      }
    } else {
      setLanguage(languageSelected);
    }
  };

  const handleButtonClick = (): void => {
    if (!isFakeData) fetchReportLanguages();
  };

  const downloadReport = async (reportContent: string): Promise<void> => {
    const reportTitle = t('reports.title');
    const year = new Date().getFullYear();
    const fileName = `${reportTitle}_${t(testRoundName)}_${year}.pdf`;

    setEventData('test_report_download', {
      test_id: testId,
      round_id: testRoundId,
      class_id: classroomId,
    });

    await downloadBase64PdfFile({
      base64Content: reportContent,
      fileName,
    });
  };

  const languages = isFakeData
    ? ['ca', 'es', 'en']
    : testReportLanguages
    ? testReportLanguages.filter(
        (lang): lang is string => lang !== null
      )
    : [];

  const reportTranslations = languages.map((language) => ({
    key: `${testRoundId}_report_${language}`,
    children: <InnerHtml text={t(`common.language.${language}`)} />,
    onSelectItem: () => handleReportLanguageItemClick(language),
    icon: 'FilePdfIcon' as IconType,
    state: STATES.ACTIVE as State,
  }));

  useEffect(() => {
    if (!language) {
      return;
    }

    if (!isFetchingReport) {
      fetchReport();
    }
  }, [language, isFetchingReport]);


  // Download the report when the report is available and reset the language
  useEffect(() => {
    if (!language) {
      return;
    }

    if (isSuccess && reportFile) {
      const reportContent = reportFile?.classroomTestRoundReport?.pdfBase64;

      if (reportContent) {
        downloadReport(reportContent);
      }

      setLanguage('');
    }

    if (isError) {
      setLanguage('');
    }
  }, [isSuccess, isError, language, reportFile]);

  if (isAvailableForDownload(report?.deliveryDate)) {
    return (
      <Popover
        trigger={
          <Button
            variant="accent"
            leftIcon="DownloadFilesIcon"
            rightIcon="ExpandMoreIcon"
            size="M"
            onClick={handleButtonClick}
          >
            {t('tests-drawer.download-report-button')}
          </Button>
        }
      >
        {isFetchingLanguages ? (
          <LoadingPopoverItem key="retrieving-reports" state="active">
            <Loader size="sm" />
            <Typography.Body2>{t('common.loadingContent')}</Typography.Body2>
          </LoadingPopoverItem>
        ) : (
          <>
            {reportTranslations.length !== 0 ? (
              <>
                {reportTranslations.map(
                  ({ children, key, state, icon, onSelectItem }) => (
                    <Popover.Item
                      key={key}
                      icon={icon}
                      state={state}
                      onSelectItem={onSelectItem}
                    >
                      {children}
                    </Popover.Item>
                  )
                )}
              </>
            ) : (
              <Popover.Item key="no-reports" state="active">
                {t('reports.message.notAvailable')}
              </Popover.Item>
            )}
          </>
        )}
      </Popover>
    );
  }

  return (
    <DisabledWrapper>
      <Button
        variant="accent"
        leftIcon="DownloadFilesIcon"
        rightIcon="ExpandMoreIcon"
        size="M"
        disabled
        noRipple
      >
        {t('tests-drawer.download-report-button')}
      </Button>
      <AvailabilityText>
        {report?.deliveryDate
          ? `${t('common.availableFrom')} ${formatDate(report.deliveryDate)}`
          : t('common.notAvailable')}
      </AvailabilityText>
    </DisabledWrapper>
  );
};

export { DownloadTestReportCell };
